import { useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Button } from '@nazka/nazka.mapframe.components';
import { useParams } from 'react-router-dom';
import * as maptilersdk from '@maptiler/sdk';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';

import { getNearbyFietssnelweg } from '@services/fietssnelwegen.service';
import { useFietssnelwegen, useNearbyStops, useStopsForBbox } from '@services/query.service';
import { getBounds } from '@services/geometry.service';
import { formatDistance } from '@services/utils.service';
import { useMapBounds, useMapZoom } from '@services/hooks.service';

import queryClient from '@App/app.query-client';

import NearbyStopPopup from '../NearbyStopPopup/NearbyStopPopup';
import FietssnelwegPopup from '../FietssnelwegPopup/FietssnelwegPopup';
import {
  createNearbyStopFeature,
  fitBoundsCoords,
  getStopsToShow,
  setFietssnelwegenLowPrio,
} from './nearby-company-section.utils';

function NearbyCompanySection({ currentJobDestination, map }) {
  const homeAddress = useSelector(state => state.map.homeAddress);
  const currentRouteInfoPanelHeight = useSelector(state => state.map.currentRouteInfoPanelHeight);
  const transportMode = useSelector(state => state.map.transportMode);

  const { vacancyId } = useParams();
  const mapBounds = useMapBounds(map);
  const mapZoom = useMapZoom(map);

  const { data: fietssnelwegen } = useFietssnelwegen();
  const { data: nearbyStops } = useNearbyStops(currentJobDestination?.lat, currentJobDestination?.lng);
  const { data: stopsNearbyHomeAddress } = useNearbyStops(homeAddress?.geometry?.coordinates?.[1], homeAddress?.geometry?.coordinates?.[0]);
  const { data: stopsForMapBounds } = useStopsForBbox(
    mapBounds?.minLat,
    mapBounds?.minLng,
    mapBounds?.maxLat,
    mapBounds?.maxLng,
    !!mapBounds && mapZoom >= 14,
  );

  const popupRef = useRef(new maptilersdk.Popup());
  const rootRef = useRef();
  const hoveredFietssnelweg = useRef(null);

  const nearbyFietssnelweg = getNearbyFietssnelweg(currentJobDestination, fietssnelwegen);

  const stopsToShow = useMemo(
    () => getStopsToShow(nearbyStops, stopsNearbyHomeAddress, stopsForMapBounds, mapZoom),
    [nearbyStops, stopsNearbyHomeAddress, stopsForMapBounds, mapZoom],
  );

  useEffect(() => {
    function onMouseMove(e) {
      if (e.features.length > 0) {
        map.setFeatureState(
          { source: 'fietssnelwegen', id: hoveredFietssnelweg.current },
          { hover: false },
        );
      }
      hoveredFietssnelweg.current = e.features[0].id;
      map.setFeatureState(
        { source: 'fietssnelwegen', id: hoveredFietssnelweg.current },
        { hover: true },
      );
    }

    function onMouseLeave() {
      if (hoveredFietssnelweg.current !== null) {
        map.setFeatureState(
          { source: 'fietssnelwegen', id: hoveredFietssnelweg.current },
          { hover: false },
        );
        hoveredFietssnelweg.current = null;
      }
    }

    if (map && fietssnelwegen) {
      map.getSource('fietssnelwegen')?.setData({
        type: 'FeatureCollection',
        features: !['e-bike', 'bike', 'bike_ov'].includes(transportMode)
          ? fietssnelwegen : setFietssnelwegenLowPrio(fietssnelwegen),
      });

      map.on('mousemove', 'fietssnelwegen-hover', onMouseMove);
      map.on('mouseleave', 'fietssnelwegen-hover', onMouseLeave);
    }

    return () => {
      map?.getSource('fietssnelwegen')?.setData({ type: 'FeatureCollection', features: [] });
      map?.off('mousemove', 'fietssnelwegen-hover', onMouseMove);
      map?.off('mouseleave', 'fietssnelwegen-hover', onMouseLeave);
    };
  }, [map, fietssnelwegen, transportMode]);

  useEffect(() => {
    if (map) {
      map.getSource('nearby-stops')?.setData({
        type: 'FeatureCollection',
        features: stopsToShow,
      });
    }
  }, [map, stopsToShow]);

  useEffect(() => {
    return () => {
      map?.getSource('nearby-stops')?.setData({ type: 'FeatureCollection', features: [] });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      popupRef.current.remove();
    };
  }, [map]);

  useEffect(() => {
    if (!map) {
      return () => { };
    }

    function handleNearbyStopClick(e) {
      const feature = e.features?.[0];

      if (!feature) {
        return;
      }

      popupRef.current.remove();
      popupRef.current.on('close', () => {
        if (rootRef.current) {
          rootRef.current.unmount();
        }
      });

      const popupNode = document.createElement('div');
      rootRef.current = createRoot(popupNode);

      const origin = { lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0] };

      rootRef.current.render(
        <QueryClientProvider client={queryClient}>
          <NearbyStopPopup
            stop={feature.properties}
            origin={origin}
            destination={currentJobDestination}
          />
        </QueryClientProvider>,
      );

      popupRef.current
        .setLngLat(origin)
        .setDOMContent(popupNode)
        .addTo(map);
    }

    map.on('click', 'nearby-stops', handleNearbyStopClick);

    return () => {
      map?.off('click', 'nearby-stops', handleNearbyStopClick);
    };
  }, [map, nearbyStops, currentJobDestination]);

  useEffect(() => {
    if (!map) {
      return () => { };
    }

    function handleFietssnelwegClick(e) {
      const feature = e.features?.[0];

      if (!feature) {
        return;
      }

      popupRef.current.remove();
      popupRef.current.on('close', () => {
        if (rootRef.current) {
          rootRef.current.unmount();
        }
      });

      const popupNode = document.createElement('div');
      rootRef.current = createRoot(popupNode);

      rootRef.current.render(
        <QueryClientProvider client={queryClient}>
          <FietssnelwegPopup feature={feature} />
        </QueryClientProvider>,
      );

      popupRef.current
        .setLngLat(e.lngLat)
        .setDOMContent(popupNode)
        .addTo(map);
    }

    map.on('click', 'fietssnelwegen-hover', handleFietssnelwegClick);

    return () => {
      map?.off('click', 'fietssnelwegen-hover', handleFietssnelwegClick);
    };
  }, [map, fietssnelwegen]);

  if (!currentJobDestination) {
    return null;
  }

  return (
    <div className={`shared-company-info__content__top__meta__nearby${!vacancyId ? ' no-margin-top' : ''}`}>
      <h4 className="shared-company-info__content__top__meta__nearby__title">
        Dichtbij deze werkgever
      </h4>
      <ul className="shared-company-info__content__top__meta__nearby__list">
        {nearbyStops?.nmbs && nearbyStops.nmbs[0] && (
          <li className="shared-company-info__content__top__meta__nearby__list__item">
            <Button
              layout="ghost"
              icon="/img/icons/train.svg"
              hover={false}
              className="shared-company-info__content__top__meta__nearby__list__item__button"
              onClick={() => {
                const features = map.querySourceFeatures('vacancy');
                document.querySelector('.shared-base__container__right')?.scrollIntoView({ behavior: 'smooth' });

                if (features.length && nearbyStops) {
                  fitBoundsCoords(map, [
                    [nearbyStops.nmbs[0].lon, nearbyStops.nmbs[0].lat],
                    features[0]?.geometry?.coordinates,
                  ], currentRouteInfoPanelHeight);
                }

                window.gtag?.('event', 'vacancy_click_nearby_train_button', {
                  event_category: 'Vacancy',
                  event_label: 'Vacancy click nearby train button',
                });
              }}
              rootElProps={{
                onMouseEnter: () => {
                  if (map && map.getSource('hovered-nearby-stop')) {
                    map.getSource('hovered-nearby-stop')?.setData({
                      type: 'FeatureCollection',
                      features: nearbyStops ? [nearbyStops.nmbs[0]].map(createNearbyStopFeature) : [],
                    });
                  }
                },
                onMouseLeave: () => {
                  if (map && map.getSource('hovered-nearby-stop')) {
                    map.getSource('hovered-nearby-stop')?.setData({
                      type: 'FeatureCollection',
                      features: [],
                    });
                  }
                },
              }}
            >
              <span>{nearbyStops.nmbs[0].name}</span>
              <span>
                op
                {' '}
                {formatDistance(nearbyStops.nmbs[0].dist)}
              </span>
            </Button>
          </li>
        )}
        {nearbyStops?.delijn && nearbyStops.delijn[0] && (
          <li>
            <Button
              layout="ghost"
              icon="/img/icons/bus.svg"
              hover={false}
              className="shared-company-info__content__top__meta__nearby__list__item__button"
              onClick={() => {
                const features = map.querySourceFeatures('vacancy');
                document.querySelector('.shared-base__container__right')?.scrollIntoView({ behavior: 'smooth' });

                if (features.length && nearbyStops) {
                  fitBoundsCoords(map, [
                    [nearbyStops.delijn[0].lon, nearbyStops.delijn[0].lat],
                    features[0]?.geometry?.coordinates,
                  ], currentRouteInfoPanelHeight);
                }

                window.gtag?.('event', 'vacancy_click_nearby_bus_button', {
                  event_category: 'Vacancy',
                  event_label: 'Vacancy click nearby bus button',
                });
              }}
              rootElProps={{
                onMouseEnter: () => {
                  if (map) {
                    map.getSource('hovered-nearby-stop')?.setData({
                      type: 'FeatureCollection',
                      features: nearbyStops ? [nearbyStops.delijn[0]].map(createNearbyStopFeature) : [],
                    });
                  }
                },
                onMouseLeave: () => {
                  if (map && map.getSource('hovered-nearby-stop')) {
                    map.getSource('hovered-nearby-stop')?.setData({
                      type: 'FeatureCollection',
                      features: [],
                    });
                  }
                },
              }}
            >
              <span>{nearbyStops.delijn[0].name}</span>
              <span>
                op
                {' '}
                {formatDistance(nearbyStops.delijn[0].dist)}
              </span>
            </Button>
          </li>
        )}
        {nearbyFietssnelweg && (
          <li>
            <Button
              layout="ghost"
              icon="/img/logo/fietssnelweg.svg"
              hover={false}
              className="shared-company-info__content__top__meta__nearby__list__item__button"
              onClick={() => {
                const features = map.querySourceFeatures('vacancy');
                const bounds = getBounds(nearbyFietssnelweg.closestFietssnelweg, features?.[0]);
                document.querySelector('.shared-base__container__right')?.scrollIntoView({ behavior: 'smooth' });

                map?.fitBounds(
                  bounds,
                  { padding: 50 },
                );

                window.gtag?.('event', 'vacancy_click_nearby_bike_highway_button', {
                  event_category: 'Vacancy',
                  event_label: 'Vacancy click nearby bike highway button',
                });
              }}
            >
              <span>
                {`Fietssnelweg ${nearbyFietssnelweg.closestFietssnelweg.properties.FSW_NUMMER}`}
              </span>
              <span>
                op
                {' '}
                {formatDistance(nearbyFietssnelweg.distanceToFietssnelweg)}
              </span>
            </Button>
          </li>
        )}
      </ul>
    </div>
  );
}

export default NearbyCompanySection;